import * as React from 'react';
import { createPortal } from 'react-dom';
import TextAncestorContext from 'react-native-web/dist/exports/Text/TextAncestorContext';

import { useIsHydrating, useLazyRef, useSafeLayoutEffect } from '@gitbook/util-react-hooks';

import { ResetInteractiveHandles } from '../Interactive';
import { ResetLayoutTheme } from '../theme';

export interface PortalAbsoluteRef {
    /** Return the DOM element wrapping this portal */
    getContainer: () => HTMLElement | null;
}

const CAN_USE_DOM = !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
);

let portalsRoot: HTMLElement | null = null;

/**
 * Return the DOM element wrapping all portals.
 */
export function getPortalsRoot(): HTMLElement {
    if (!CAN_USE_DOM) {
        throw new Error(
            'getPortalsRoot should only be called on the client (in an effect or event handler)'
        );
    }

    if (!portalsRoot) {
        const found = document.getElementById('portals-root');

        if (found) {
            portalsRoot = found;
        } else {
            portalsRoot = document.createElement('div');
            portalsRoot.setAttribute('id', 'portals-root');

            document.body.appendChild(portalsRoot);
        }
    }

    return portalsRoot;
}

/**
 * Use a DIV in the dom that can be used for rendering a portal.
 * The DOM element is created at render time to ensure the portal content can directly be rendered in the react tree.
 * The DOM element is appended to the DOM only when the layout effect is run.
 */
export function usePortalContainer(): HTMLElement | null {
    // With React Async, react can throw away render states (refs, states) while re-rendering the component
    // to ensure we don't leak DOM elements, we only append to the DOM when the layout effect is run,
    // the other DOM elements will be garbage collected.
    // But we create the DOM element at render time to ensure the portal content can directly be rendered in the react tree.
    //
    // To ensure the nesting order of the React tree is respected (portal created by descendants should be rendered after in the DOM),
    // we keep track of the latest portal container created and append the new portal container after it.
    // This approach is needed because useLayourEffect is called in children before parents.
    const { container, at } = useLazyRef(() => {
        if (!CAN_USE_DOM) {
            return { container: null, at: null };
        }

        const root = getPortalsRoot();

        return {
            at: root.lastElementChild,
            container: document.createElement('div'),
        };
    }).current;

    // Add the portal to the DOM
    // and remove the element on unmount
    useSafeLayoutEffect(() => {
        if (!container) {
            return;
        }

        const root = getPortalsRoot();

        if (at) {
            root.insertBefore(container, at.nextSibling);
        } else {
            root.appendChild(container);
        }

        return () => {
            root.removeChild(container);
        };
    }, [container, at]);

    return container;
}

/*
 * Primitive portal component to render an absolute portal.
 */
const PortalAbsolute = React.forwardRef(
    (
        props: {
            children: React.ReactNode;
            /**
             * Optional container in which the portal should be rendered.
             * Ex: when a palette is inside a parent portal, it needs to be
             * rendered inside its parent portal container to handle events correctly.
             */
            container?: HTMLElement | null;
        },
        ref: React.Ref<PortalAbsoluteRef>
    ): React.ReactElement | null => {
        const { children, container: providedContainer } = props;

        const portalContainer = usePortalContainer();
        const container = providedContainer ?? portalContainer ?? document.body;
        const hydrating = useIsHydrating();

        React.useImperativeHandle(ref, () => ({
            // Get the portal container,
            getContainer: () => container,
        }));

        if (!container || hydrating) {
            return null;
        }

        // we only create the portal when the component is mounted
        // otherwise it creates issues with rehydratation (https://github.com/facebook/react/issues/13097#issuecomment-405658104)
        return (
            <ResetLayoutTheme>
                {/* We reset the RNW internal context to indicate that Text in the portal are not considered
                    as nested into a Text when the portal is rendered in a Text */}
                <TextAncestorContext.Provider value={false}>
                    <ResetInteractiveHandles>
                        {createPortal(children, container)}
                    </ResetInteractiveHandles>
                </TextAncestorContext.Provider>
            </ResetLayoutTheme>
        );
    }
);
PortalAbsolute.displayName = 'PortalAbsolute';

export { PortalAbsolute };
